<template>
  <nav class="the-mobile-nav" :class="className">
    <div ref="mask" class="the-mobile-nav__mask"
      @mousedown="onDragTrigger"
      @touchstart="onDragTrigger">
      <ul class="the-mobile-nav__list" :style="listStyle"><!--
      --><li ref="item" class="the-mobile-nav__list-item" v-for="{ year } in years" :key="year">
          <a class="the-mobile-nav__btn"
            :class="{ 'the-mobile-nav__btn--active': (year === $route.params.year) }"
            :href="year"
            :title="year"
            @click.prevent="onNavItemClick($event, year)"><!--
          --><span class="the-mobile-nav__btn-label">{{ year }}</span>
          </a>
        </li>
      </ul>
    </div>
    <button class="the-mobile-nav__arrow the-mobile-nav__arrow--prev"
      :class="arrowPrevClassName"
      title="往左捲動"
      @click="onPrevClick">prev</button>
    <button class="the-mobile-nav__arrow the-mobile-nav__arrow--next"
      :class="arrowNextClassName"
      title="往右捲動"
      @click="onNextClick">next</button>
  </nav>
</template>

<script>
import { ELECTION_NAV_CONFIG } from '@/constant'

export default {
  name: 'TheMobileNav',
  data () {
    return {
      data: ELECTION_NAV_CONFIG,
      isDragging: false,
      maskWidth: 0,
      listWidth: 0,
      itemWidth: 70,
      dragOverDebounceRange: 50,
      minDragX: 0,
      maxDragX: 0,
      startDragTime: 0,
      startDragPoint: { x: 0, y: 0 }, // dragging start point
      lastDragPoint: { x: 0, y: 0 }, // last dragging point
      lastDraggingDistance: { dx: 0, dy: 0 }, // last dragging distance
      listRenderPoint: { x: 0, y: 0 }, // dragging render point
      listOriginPoint: { x: 0, y: 0 }, // result point after stop dragging and boundary correction
      scrollItemLength: 2,
      clickTimeThreshold: 300, // unit: ms
      clickDistanceThreshold: 5 // unit: px
    }
  },
  computed: {
    className () {
      return {
        'the-mobile-nav--scrollable': this.isScrollable
      }
    },
    arrowPrevClassName () {
      return {
        'the-mobile-nav__arrow--disable': !this.canScrollPrev
      }
    },
    arrowNextClassName () {
      return {
        'the-mobile-nav__arrow--disable': !this.canScrollNext
      }
    },
    listStyle () {
      if (!this.isScrollable) {
        return {
          'text-align': 'center'
        }
      }
      let transform = `translateX(${this.listRenderPoint.x}px)`
      let transition = !this.isDragging ? 'transform 0.4s' : 'none'
      return {
        transform,
        transition
      }
    },
    years () {
      return this.data
        .filter(({ disable }) => !disable)
    },
    currentYear () {
      return this.$route.params.year
    },
    currentYearIndex () {
      return this.years
        .map(({ year }) => year)
        .indexOf(this.currentYear)
    },
    isScrollable () {
      return this.listWidth > this.maskWidth
    },
    canScrollPrev () {
      return this.listOriginPoint.x < this.maxDragX
    },
    canScrollNext () {
      return this.listOriginPoint.x > this.minDragX
    }
  },
  methods: {
    onDragTrigger (event) {
      // prevent default dom dragging behavior
      if (event.type === 'mousedown') {
        event.preventDefault()
      }
      this.$bus.$on('drag:start', this.onDragStart)
    },
    onDragStart (point) {
      // console.log('drag:start', point)
      this.isDragging = true
      this.startDragTime = Date.now()
      this.startDragPoint = { ...point }
      this.$bus.$on('drag:move', this.onDragMove)
      this.$bus.$on('drag:stop', this.onDragStop)
    },
    onDragMove (point) {
      // console.log('drag:move', point)
      this.updateDragPoints(point)
    },
    onDragStop (point) {
      // console.log('drag:stop', point)
      this.clearDragListeners()
      this.updateDragPoints(point)
      this.listOriginPoint = this.listRenderPoint
      this.correctBoundary()
    },
    clearDragListeners () {
      this.isDragging = false
      this.$bus.$off('drag:start', this.onDragStart)
      this.$bus.$off('drag:move', this.onDragMove)
      this.$bus.$off('drag:stop', this.onDragStop)
    },
    onResize ({ width, height }) {
      // console.log(`${width} x ${height}`)
      this.correctBoundary(true)
    },
    onNavItemClick (event, year) {
      const clickProcessTime = Date.now() - this.startDragTime
      // ignore click event after dragging
      if (clickProcessTime > this.clickTimeThreshold) {
        console.log('click interrupted by time')
        return
      }
      if (Math.abs(this.lastDraggingDistance.dx) > this.clickDistanceThreshold) {
        console.log('click interrupted by distance')
        return
      }
      // ignore the same navigation path
      if (this.$route.params.year !== year) {
        this.$router.push({
          name: 'home',
          params: { year }
        })
      }
      // clear drag listeners manually
      this.clearDragListeners()
      // make active item viewable in boundary
      this.correctBoundary(true)
    },
    onPrevClick (event) {
      this.canScrollPrev && this.scroll(-this.scrollItemLength)
    },
    onNextClick (event) {
      this.canScrollNext && this.scroll(+this.scrollItemLength)
    },
    updateDragPoints (point) {
      let { x: ox, y: oy } = this.listOriginPoint
      let dx = point.x - this.startDragPoint.x
      let dy = point.y - this.startDragPoint.y
      let x = ox + dx
      let y = oy + dy
      if (x < this.minDragX - this.dragOverDebounceRange) {
        x = this.minDragX - this.dragOverDebounceRange
      } else if (x > this.maxDragX + this.dragOverDebounceRange) {
        x = this.maxDragX + this.dragOverDebounceRange
      }
      this.lastDraggingDistance = { dx, dy }
      this.lastDragPoint = { ...point }
      this.listRenderPoint = { x, y }
    },
    correctBoundary (focusOnActive = false) {
      this.$nextTick(() => {
        let maskBounds = this.$refs.mask.getBoundingClientRect()
        this.maskWidth = maskBounds.width
        this.listWidth = Math.round(this.itemWidth * this.years.length)
        if (!this.isScrollable) {
          return
        }
        this.minDragX = this.maskWidth - this.listWidth
        // console.log(`${this.minDragX} ~ ${this.maxDragX}`)
        let point = { ...this.listRenderPoint }
        // auto focus on active item
        if (focusOnActive) {
          let minActiveX = -this.currentYearIndex * this.itemWidth
          let maxActiveX = minActiveX + Math.floor((this.maskWidth - this.itemWidth) / this.itemWidth) * this.itemWidth
          if (point.x <= minActiveX) {
            point.x = Math.round(minActiveX)
          } else if (point.x >= maxActiveX) {
            point.x = Math.round(maxActiveX)
          } else {
            point.x = Math.round(point.x / this.itemWidth) * this.itemWidth
          }
          // console.log(this.currentYear, minActiveX, point.x, maxActiveX)
        } else {
          // limited the position in multiples times of item unit width
          point.x = Math.round(point.x / this.itemWidth) * this.itemWidth
        }
        // limited the position in boundary
        if (point.x > this.maxDragX) {
          point.x = this.maxDragX
        } else if (point.x < this.minDragX) {
          point.x = this.minDragX
        }
        this.listOriginPoint = this.listRenderPoint = { ...point }
      })
    },
    scroll (offset = 0) {
      let offsetX = this.itemWidth * offset
      let point = { ...this.listRenderPoint }
      point.x -= offsetX
      this.listRenderPoint = { ...point }
      this.correctBoundary()
    }
  },
  mounted () {
    this.$bus.$on('resize', this.onResize)
    this.correctBoundary(true)
  }
}
</script>

<style lang="scss">
.the-mobile-nav {
  $comp: &;
  $arrow-width: 36px;
  @include use-layer(&);
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 44px;
  background: #1a1a1a;
  border-top: 1px solid #1a1a1a;
  border-bottom: 1px solid #1a1a1a;
  user-select: none;
  transform: translateY(0);
  transition: transform 0.4s;
  @include rwd($RWD_TABLET) {
    transform: translateY(150%);
  }

  &__mask {
    position: absolute;
    left: $arrow-width;
    right: $arrow-width;
    height: 100%;
    overflow: hidden;
  }

  &__list {
    position: relative;
    height: 100%;
    white-space: nowrap;
    backface-visibility: hidden;
    transition: transform 0.4s;

    &-item {
      display: inline-block;
      position: relative;
      width: 70px;
      height: 100%;
      color: white;
      vertical-align: top;
    }
  }

  &__btn {
    display: block;
    position: relative;
    width: 100%;
    height: 100%;
    color: inherit;
    text-align: center;
    background: #1a1a1a;
    transition: background-color 0.5s;
    cursor: pointer;
    @include use-vertical-align();

    &:active {
      padding-top: 2px;
    }

    &-label {
      vertical-align: middle;
      font-size: 1.125em;
    }

    &--active {
      color: #1a1a1a;
      background: white;
      transition-duration: 0.3s;
    }
  }

  &__arrow {
    display: none;
  }

  &--scrollable {
    #{$comp}__list {
      position: relative;
      height: 100%;
      white-space: nowrap;
      backface-visibility: hidden;
      transition: transform 0.4s;

      &-item {
        &:first-child,
        &:last-child {
          &:after {
            content: "";
            display: block;
            position: absolute;
            top: 50%;
            width: 50px;
            height: 0;
            border-top: 1px solid #555555;
          }
        }

        &:first-child {
          &:after {
            left: -50px;
          }
        }

        &:last-child {
          &:after {
            right: -50px;
          }
        }
      }
    }

    #{$comp}__arrow {
      display: block;
      position: absolute;
      top: 0;
      width: $arrow-width;
      height: 100%;
      color: transparent;
      font-size: 0;
      background: #1a1a1a;
      cursor: pointer;

      &:active {
        &:before {
          margin-top: -2px;
        }
      }

      &:before {
        content: "";
        display: block;
        position: absolute;
        top: 50%;
        left: 50%;
        width: 0;
        height: 0;
        margin: -3px 0 0 -6px;
        border-width: 0 6px 6px 6px;
        border-color: transparent transparent white transparent;
        border-style: solid;
      }

      &--prev {
        left: 0;
        &:before {
          transform: rotate(-90deg);
        }
      }

      &--next {
        right: 0;
        &:before {
          transform: rotate(+90deg);
        }
      }

      &--disable {
        cursor: default;

        &:before {
          border-color: transparent transparent #555555 transparent;
        }

        &:active {
          &:before {
            margin-top: -3px;
          }
        }
      }
    }
  }
}

.desktop {
  $comp: ".the-mobile-nav";

  #{$comp} {
    &__btn {
      &:hover {
        color: #1a1a1a;
        background: #b4b4b4;
      }
    }

    &--scrollable {
      #{$comp}__arrow {
        transition: background-color 0.3s;

        &:hover {
          background: #b4b4b4;

          &:before {
            border-color: transparent transparent #1a1a1a transparent;
          }
        }

        &--disable {
          &:hover {
            background: #1a1a1a;

            &:before {
              border-color: transparent transparent #555555 transparent;
            }
          }
        }
      }
    }
  }
}
</style>
